import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios'; // Import axios for making API requests
import performanceMonitor from './utils/performanceMonitor';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [connectionId, setConnectionId] = useState(null);
  const [connectionQuality, setConnectionQuality] = useState('good');
  const socketRef = useRef(null);
  const clientIdRef = useRef(sessionStorage.getItem('clientId') || uuidv4());
  const messageQueueRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    const maxReconnectAttempts = 5;
    let reconnectAttempts = 0;

    if (!localStorage.getItem('clientId')) {
      localStorage.setItem('clientId', clientIdRef.current);
    }

    const sendQueuedMessages = () => {
      while (messageQueueRef.current.length > 0 && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        try {
          const message = messageQueueRef.current.shift();
          socketRef.current.send(message);
        } catch (error) {
          console.error('Error sending queued message:', error);
          // Stop trying to send more messages if we hit an error
          break;
        }
      }
    };

    const getReconnectDelay = (attempt) => {
      // Exponential backoff with jitter: 1s, 2s, 4s, 8s, 16s, max 30s
      const baseDelay = Math.min(1000 * Math.pow(2, attempt), 30000);
      const jitter = Math.random() * 1000; // Add up to 1s of jitter
      return baseDelay + jitter;
    };


    const clearReconnectTimeout = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const getUserFromToken = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (error) {
        console.error('Error parsing JWT token:', error);
        return null;
      }
    };

    const createPatchPayload = (newConnectionId) => {
      const payload = {};
    
      // Include all fields regardless of their value
      payload.clientId= newConnectionId || '';

      return payload;
    };

    const updateUserClientId = async (newConnectionId) => {
      try {
        const session = await fetchAuthSession();
        const jwtToken = session?.idToken?.jwtToken;
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
  
        const user = getUserFromToken(token);
  
        if (!user) {
          alert('Failed to retrieve user from JWT token.');
          return;
        }
  
        const headers = {
          'Content-type': 'application/json; charset=UTF-8',
          'Authorization': `Bearer ${token}`,
        };
  
        const apiUrl = process.env.REACT_APP_API_URL; 
        const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
        const patchData = createPatchPayload(newConnectionId);
  
        await axios.patch(url, patchData, { headers });

        console.log(`User updated with new clientId: ${newConnectionId}`);
      } catch (error) {
        console.error('Error updating user with new clientId:', error);
      }
    };

    // Helper function to handle authentication errors and redirect
    const handleAuthError = async (errorMessage) => {
      console.error('Authentication error detected:', errorMessage);
      try {
        await signOut();
      } catch (signOutError) {
        console.error('Error signing out:', signOutError);
      }
      // Stop reconnection attempts
      reconnectAttempts = maxReconnectAttempts;
      clearReconnectTimeout();
      // Close WebSocket if open
      if (socketRef.current) {
        socketRef.current.close();
      }
      // Redirect to login page
      window.location.href = '/auth';
    };

    const connectWebSocket = async () => {
      if (socketRef.current && (socketRef.current.readyState === WebSocket.CONNECTING || socketRef.current.readyState === WebSocket.OPEN)) {
        console.log('WebSocket is already connecting or connected, readyState:', socketRef.current.readyState);
        return;
      }

      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.idToken?.toString();
        
        // Check if token is available
        if (!token) {
          console.error('No authentication token available for WebSocket connection');
          await handleAuthError('No authentication token available');
          return;
        }
        
        const wsEnv = process.env.REACT_APP_SOCKET_API_URL; 
        const wsUrl = `${wsEnv}?Authorization=${encodeURIComponent(token)}`;

        socketRef.current = new WebSocket(wsUrl);

        socketRef.current.onopen = () => {
          try {
            console.log('WebSocket onopen event fired, readyState:', socketRef.current.readyState);
            
            // Send authentication with a small delay to ensure WebSocket is fully ready
            setTimeout(() => {
              if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                const authMessage = JSON.stringify({ action: 'authenticate', token, clientId: clientIdRef.current });
                socketRef.current.send(authMessage);
                console.log('WebSocket connection established, awaiting connection ID from server...');
                reconnectAttempts = 0; // Reset reconnect attempts after a successful connection
                sendQueuedMessages();
              } else {
                console.error('WebSocket not ready after onopen event');
              }
            }, 100); // Small delay to ensure WebSocket is fully ready
          } catch (error) {
            console.error('Error during WebSocket authentication:', error);
          }
        };

        socketRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocketContext received message:', data);
            
            // Check for authentication errors in the message
            if (data.error || data.type === 'error') {
              const errorMessage = data.error || data.message || JSON.stringify(data);
              if (errorMessage.includes('NotAuthorizedException') || 
                  errorMessage.includes('Unauthenticated') || 
                  errorMessage.includes('401') || 
                  errorMessage.includes('403') ||
                  errorMessage.includes('Unauthorized') ||
                  errorMessage.includes('forbidden') ||
                  errorMessage.includes('Access to Identity')) {
                handleAuthError(errorMessage);
                return;
              }
            }
            
            if (data.connectionId || data.clientId) {
              const receivedId = data.connectionId || data.clientId;
              setConnectionId(receivedId);
              console.log('Connection ID received from server: ' + receivedId);
              // Update the user with the new clientId
              updateUserClientId(receivedId);

            } else {
              console.error('No connectionId found in the server response.');
            }
          } catch (error) {
            console.error('Error parsing server response:', error);
          }
        };

        socketRef.current.onclose = (event) => {
          clearReconnectTimeout(); // Clear any existing reconnect timeout
          
          // Check if close was due to authentication error (code 1008 = policy violation, often auth-related)
          if (event.code === 1008 || event.reason?.includes('NotAuthorizedException') || 
              event.reason?.includes('Unauthenticated') || event.reason?.includes('Unauthorized') ||
              event.reason?.includes('forbidden') || event.reason?.includes('Access to Identity')) {
            console.error('WebSocket closed due to authentication error:', event.reason);
            handleAuthError(event.reason || 'WebSocket authentication failed').catch(err => {
              console.error('Error handling auth error:', err);
            });
            return;
          }
          
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = getReconnectDelay(reconnectAttempts - 1);
            console.log(`Reconnecting... attempt ${reconnectAttempts} in ${Math.round(delay)}ms`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, delay);
          } else {
            console.error('Max reconnect attempts reached. WebSocket connection failed.');
            setConnectionQuality('disconnected');
          }
        };

        socketRef.current.onerror = async (error) => {
          const errorMessage = error.message || error.toString() || 'WebSocket connection error';
          console.error('WebSocket error:', errorMessage);
          
          // Check if it's an authentication error
          if (errorMessage.includes('NotAuthorizedException') || 
              errorMessage.includes('Unauthenticated') || 
              errorMessage.includes('401') || 
              errorMessage.includes('403') ||
              errorMessage.includes('Unauthorized') ||
              errorMessage.includes('identity pool') ||
              errorMessage.includes('forbidden') ||
              errorMessage.includes('Access to Identity')) {
            await handleAuthError(errorMessage);
          }
        };
      } catch (error) {
        const errorMessage = error.message || error.toString() || 'Error establishing WebSocket connection';
        console.error('Error establishing WebSocket connection:', errorMessage);
        
        // Check if it's an authentication error (including Cognito Identity Pool errors)
        if (errorMessage.includes('NotAuthorizedException') || 
            errorMessage.includes('Unauthenticated') || 
            errorMessage.includes('401') || 
            errorMessage.includes('403') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('identity pool') ||
            errorMessage.includes('forbidden') ||
            errorMessage.includes('Access to Identity') ||
            (error.response && (error.response.status === 400 || error.response.status === 401 || error.response.status === 403))) {
          await handleAuthError(errorMessage);
          return; // Don't attempt to reconnect
        }
      }
    };

    connectWebSocket();

    return () => {
      // Comprehensive cleanup to prevent memory leaks
      clearReconnectTimeout();
      
      if (socketRef.current) {
        // Remove all event listeners before closing
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        
        // Close the connection
        if (socketRef.current.readyState === WebSocket.OPEN || 
            socketRef.current.readyState === WebSocket.CONNECTING) {
          socketRef.current.close();
        }
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ 
      socket: socketRef.current, 
      clientId: clientIdRef.current, 
      connectionId, 
      connectionQuality 
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
